/**
 * Pure state helpers for the All Team Members directory
 * (`plans/all-team-members-directory.md`, "The read model").
 *
 * Postgres decides which rows exist; each row then binds its memberships to
 * Firestore and stays live. Everything the row component needs to decide from
 * those two sources lives here, so it can be specified without mounting a
 * `.vue` file: the request builder (table options -> endpoint request), the
 * row-state reducer (skeleton, live, removed), and the display derivation that
 * prefers the live docs and falls back to the Postgres row until they resolve.
 */
import moment from 'moment-timezone';
import {
	MemberRow,
	MemberTeamRef,
	MembersFetchResult,
	MembersOrder,
	MembersRequest,
	MembersSort,
	MembersStatus,
	MEMBERS_DEFAULT_ORDER,
	MEMBERS_DEFAULT_SORT,
	clampMembersLimit,
} from '@/utils/membersApi';
import { BulkCompanyRoleTarget } from '@/views/Team/CompanyUserRoleDialogs/companyUserRoleDialogState';
import { PanelFilters } from '@/utils/panelFilters';
import {
	CURSOR_WALK_START,
	CursorWalk,
	FetchFailureInput,
	MIRROR_LAG_REFETCH_MS,
	WalkFooterPagination,
	canWalkBack,
	canWalkForward,
	fetchFailureKeepsRows,
	restartWalk,
	walkAfterPage,
	walkBack,
	walkFooterPageText,
	walkFooterPagination,
	walkForward,
	walkPageNumber,
} from '@/utils/cursorWalk';

/**
 * The shape Vuetify's `v-data-table` hands back through `options.sync`. Only
 * the fields the endpoint can act on are read; the rest (groupBy, multiSort)
 * are ignored.
 */
export interface MembersDirectoryTableOptions {
	itemsPerPage?: number;
	sortBy?: string[];
	sortDesc?: boolean[];
}

// The walk and its footer are shared by every cursor-paged table. The names
// below are the ones this directory has always exported.
export type MembersDirectoryWalk = CursorWalk;
export const MEMBERS_WALK_START: MembersDirectoryWalk = CURSOR_WALK_START;
export {
	restartWalk,
	walkAfterPage,
	canWalkBack,
	canWalkForward,
	walkForward,
	walkBack,
	walkPageNumber,
	walkFooterPagination,
	walkFooterPageText,
	fetchFailureKeepsRows,
	MIRROR_LAG_REFETCH_MS,
};
export type { WalkFooterPagination, FetchFailureInput };

/**
 * Header `value`s of the directory table. Sorting is by header value, and the
 * map below is the only place a header value meets an endpoint sort name.
 */
export const MEMBERS_DIRECTORY_HEADER_VALUE = {
	EMAIL: 'email',
	TEAMS: 'teams',
	COMPANY_ROLE: 'companyRole',
	JOINED: 'joined',
	ACTIONS: 'actions',
} as const;

const SORT_BY_HEADER_VALUE: Record<string, MembersSort> = {
	[MEMBERS_DIRECTORY_HEADER_VALUE.EMAIL]: 'email',
	[MEMBERS_DIRECTORY_HEADER_VALUE.JOINED]: 'joined',
	[MEMBERS_DIRECTORY_HEADER_VALUE.TEAMS]: 'membership_count',
};

export const MEMBERS_DIRECTORY_ITEMS_PER_PAGE_OPTIONS: number[] = [25, 50, 100];
export const MEMBERS_DIRECTORY_DEFAULT_ITEMS_PER_PAGE: number = MEMBERS_DIRECTORY_ITEMS_PER_PAGE_OPTIONS[0];

/**
 * The members directory teams column shows at most this many chips before
 * folding the rest into a "+N more" chip that opens their member dashboard.
 */
export const MEMBERS_DIRECTORY_MAX_TEAM_CHIPS: number = 10;

/**
 * Options for every `$bind` whose key is dotted (`memberships.{teamId}`),
 * spread into `get$bindFirestoreOptions`. The house
 * default is `wait: true`, which makes the vendored vuefire stage the first
 * snapshot in an empty buffer; walking a dotted key into that buffer throws,
 * so the value is never written and the bind never settles. The maps are
 * pre-seeded with every key, so writing straight into the component is
 * reactive and nothing needs the buffer.
 * Pinned by `__tests__/unit/vuefire/bindDocumentDottedKey.spec.ts`.
 */
export const KEYED_BIND_OPTIONS: { wait: false } = { wait: false };

export interface MembersDirectoryFilters {
	searchQuery?: string | null;
	statusFilter?: MembersStatus | string | null;
	companyRoleFilter?: string | null;
	teamFilter?: string | null;
}

export interface BuildMembersRequestInput extends MembersDirectoryFilters {
	companyId: string;
	options: MembersDirectoryTableOptions | null | undefined;
	// Where the walk is: `MembersDirectoryWalk.cursor`, or null for page one.
	cursor?: string | null;
	// The caller's selected team, which names the account the request acts as.
	// Every other api2 call sends it; see buildMembersQueryParams.
	accountTeamId?: string | null;
}

function trimmedOrNull(value: string | null | undefined): string | null {
	return value && value.trim() ? value.trim() : null;
}

/**
 * True when any filter narrows the directory, so an empty page can say "no
 * match" instead of "nobody here yet".
 */
export function hasActiveMembersFilters(filters: MembersDirectoryFilters): boolean {
	return !!(
		trimmedOrNull(filters.searchQuery) ||
		isMembersStatus(filters.statusFilter) ||
		trimmedOrNull(filters.companyRoleFilter) ||
		trimmedOrNull(filters.teamFilter)
	);
}

function isMembersStatus(value: unknown): value is MembersStatus {
	return value === 'pending' || value === 'joined';
}

/**
 * The filters the Filters panel holds: every one but the search, which stays in
 * the toolbar and applies as it is typed. The panel edits a copy of these (the
 * draft), and only Apply commits it (FiltersPanelMixin, panelFilters.ts). All
 * three rest empty.
 */
export interface MembersPanelFilters extends PanelFilters {
	// Any text: the request reads only a status the endpoint knows (buildMembersRequest)
	statusFilter: string | null;
	companyRoleFilter: string | null;
	teamFilter: string | null;
}

export const MEMBERS_RESTING_FILTERS: MembersPanelFilters = {
	statusFilter: null,
	companyRoleFilter: null,
	teamFilter: null,
};

/**
 * Turn the table's options, the page's filters and the walk's position into one
 * endpoint request. Unknown sort headers fall back to the default sort, and the
 * page size is clamped to the endpoint's window so a stale remembered
 * pagination never produces a 400.
 */
export function buildMembersRequest(input: BuildMembersRequestInput): MembersRequest {
	const options: MembersDirectoryTableOptions = input.options || {};

	const limit: number = clampMembersLimit(
		options.itemsPerPage === undefined || options.itemsPerPage === null
			? MEMBERS_DIRECTORY_DEFAULT_ITEMS_PER_PAGE
			: options.itemsPerPage,
	);

	const sortHeader: string | undefined = Array.isArray(options.sortBy) ? options.sortBy[0] : undefined;
	const sort: MembersSort = (sortHeader && SORT_BY_HEADER_VALUE[sortHeader]) || MEMBERS_DEFAULT_SORT;

	// Only a sort the endpoint knows can carry a direction; an unknown header
	// falls back to the default sort in its default direction.
	const sortIsKnown: boolean = !!(sortHeader && SORT_BY_HEADER_VALUE[sortHeader]);
	const desc: boolean = sortIsKnown && Array.isArray(options.sortDesc) && options.sortDesc[0] === true;
	const order: MembersOrder = desc ? 'desc' : MEMBERS_DEFAULT_ORDER;

	const q: string | null = trimmedOrNull(input.searchQuery);
	const status: MembersStatus | null = isMembersStatus(input.statusFilter) ? input.statusFilter : null;
	const companyRoleId: string | null = trimmedOrNull(input.companyRoleFilter);
	// Travels as `in_team_id`. The account selection is separate and is
	// `accountTeamId`, so filtering never changes who the caller acts as.
	const teamId: string | null = trimmedOrNull(input.teamFilter);

	return {
		companyId: input.companyId,
		cursor: input.cursor || null,
		limit,
		sort,
		order,
		q,
		status,
		companyRoleId,
		teamId,
		accountTeamId: trimmedOrNull(input.accountTeamId),
		// The directory is a view of the company as it stands, so it asks for
		// the activated reading rather than the endpoint's both. Without this
		// the table would list people whose only team has been deactivated and
		// its total would stop matching the dashboard's card.
		isActive: true,
	};
}

/**
 * The filters as the directory remembers them beside its pagination, so the
 * page number it restores is applied to the result set that produced it.
 */
export interface RememberedMembersDirectoryFilters {
	searchQuery: string;
	statusFilter: string | null;
	companyRoleFilter: string | null;
	teamFilter: string | null;
}

export interface RememberedMembersDirectoryView {
	// The cursor that produced the page being left, so coming back lands on it
	// rather than at the top. Null on the first page.
	cursor: string | null;
	// That page's number, so the footer still counts its rows from the right
	// place ("26-50") once it is restored. 1 on the first page.
	page: number;
	itemsPerPage: number;
	sortBy: string[];
	sortDesc: boolean[];
	filters: RememberedMembersDirectoryFilters;
}

/**
 * What the directory stores on the way out: the table options, the four
 * filters, and where the walk had got to. A remembered position is meaningless
 * without the search, filters and sort that produced it (a cursor belongs to
 * one listing and the endpoint refuses it in any other), so they always travel
 * together and are restored together.
 */
export function rememberMembersDirectoryView(
	options: MembersDirectoryTableOptions | null | undefined,
	filters: MembersDirectoryFilters | null | undefined,
	cursor?: string | null,
	page?: number | null,
): RememberedMembersDirectoryView {
	const o: MembersDirectoryTableOptions = options || {};
	const f: MembersDirectoryFilters = filters || {};
	return {
		cursor: cursor || null,
		page: cursor ? wholePageNumber(page) : 1,
		itemsPerPage: Number(o.itemsPerPage) || MEMBERS_DIRECTORY_DEFAULT_ITEMS_PER_PAGE,
		sortBy: Array.isArray(o.sortBy) ? [...o.sortBy] : [],
		sortDesc: Array.isArray(o.sortDesc) ? [...o.sortDesc] : [],
		filters: {
			searchQuery: f.searchQuery || '',
			statusFilter: f.statusFilter || null,
			companyRoleFilter: f.companyRoleFilter || null,
			teamFilter: f.teamFilter || null,
		},
	};
}

/** A filter's stored value: text, or nothing. */
function storedText(value: unknown): string | null {
	return typeof value === 'string' && value ? value : null;
}

/** A page number from anywhere: a whole number of at least 1, or 1. */
function wholePageNumber(value: unknown): number {
	const page: number = Math.floor(Number(value));
	return Number.isFinite(page) && page >= 1 ? page : 1;
}

/**
 * The same view coming back OUT of the store, where nothing is trusted.
 * `StoredLastPagination` is shared by every server-paged table, so it types its
 * filters as a loose record; a spec (and `rememberMembersDirectoryView`) hands
 * over the exact shape instead. Both are read the same way, key by key.
 */
export interface StoredMembersDirectoryView {
	cursor?: string | null;
	page?: number | null;
	itemsPerPage?: number;
	sortBy?: string[];
	sortDesc?: boolean[];
	filters?: RememberedMembersDirectoryFilters | Record<string, unknown> | null;
}

export interface HydratedMembersDirectoryView {
	options: MembersDirectoryTableOptions;
	// null when nothing usable was stored, so the caller leaves its filters alone.
	filters: RememberedMembersDirectoryFilters | null;
	// The walk to resume, restored only with the filters that produced it. Its
	// `previous` is always empty: the stack of cursors behind a page is not
	// stored, so coming back restores the page (and its number, as `startPage`)
	// but its Back button starts the walk over. The alternative is storing an
	// unbounded list of positions for a table the person may never return to.
	walk: MembersDirectoryWalk;
}

/**
 * The options, filters and walk position to restore from what was stored, on
 * top of the defaults. Three rules, each pinning a reviewed defect:
 *  - the sort is restored only when it names a column the endpoint can sort
 *    by. Vuetify's third header click leaves `sortBy: []`, and restoring that
 *    would show no sort arrow while the request sorts by email;
 *  - the filters are restored whenever any were stored;
 *  - the cursor is restored only together with the filters and sort that
 *    produced it, because it belongs to that listing and no other. Should it
 *    have gone stale anyway, the endpoint answers INVALID_CURSOR and the page
 *    restarts the walk.
 */
export function hydrateMembersDirectoryView(
	stored: StoredMembersDirectoryView | null | undefined,
	defaults: MembersDirectoryTableOptions,
): HydratedMembersDirectoryView {
	const options: MembersDirectoryTableOptions = { ...defaults };
	if (!stored) return { options, filters: null, walk: restartWalk() };

	const itemsPerPage: number = Math.floor(Number(stored.itemsPerPage));
	if (Number.isFinite(itemsPerPage) && itemsPerPage > 0) options.itemsPerPage = itemsPerPage;

	const sortHeader: string | undefined = Array.isArray(stored.sortBy) ? stored.sortBy[0] : undefined;
	if (sortHeader && SORT_BY_HEADER_VALUE[sortHeader]) {
		options.sortBy = [sortHeader];
		options.sortDesc = [Array.isArray(stored.sortDesc) && stored.sortDesc[0] === true];
	}

	const storedFilters: Record<string, unknown> | null =
		stored.filters && typeof stored.filters === 'object' ? stored.filters as Record<string, unknown> : null;
	if (!storedFilters) return { options, filters: null, walk: restartWalk() };

	const cursor: string | null = typeof stored.cursor === 'string' && stored.cursor ? stored.cursor : null;

	return {
		options,
		walk: { ...restartWalk(), cursor, startPage: cursor ? wholePageNumber(stored.page) : 1 },
		filters: {
			searchQuery: typeof storedFilters.searchQuery === 'string' ? storedFilters.searchQuery : '',
			statusFilter: storedText(storedFilters.statusFilter),
			companyRoleFilter: storedText(storedFilters.companyRoleFilter),
			teamFilter: storedText(storedFilters.teamFilter),
		},
	};
}

/**
 * What the snackbar says about a failed fetch. The server's own words are
 * used for a domain lockdown (`DOMAIN_LOCKED`: "usage of this domain is
 * restricted to <company>, switch workspace"), because the generic access
 * line would send the person looking for a permission they are not missing.
 * Every other failure gets the page's own line for its reason.
 */
export function membersFetchFailureMessage(
	result: MembersFetchResult,
	messageByReason: Record<string, string>,
): string {
	if (result.ok === true) return '';
	const failure: Exclude<MembersFetchResult, { ok: true }> = result;
	if (failure.code === 'DOMAIN_LOCKED' && failure.message) return failure.message;
	return messageByReason[failure.reason] || messageByReason.failed;
}

export interface TeamFilterItem {
	id: string;
	name: string;
}

/**
 * The team filter's choices. The directory never binds `teams/{id}` (a regular
 * member may not read it), so the picker is fed from what the endpoint has
 * already handed this viewer: every team named on any page fetched so far.
 * Merging keeps a team on the list after the filter narrows the rows to
 * people who are not on it; a newer name for the same id wins (a rename shows
 * after the mirror lag). Sorted by name for the picker.
 */
export function mergeTeamFilterItems(
	existing: TeamFilterItem[] | null | undefined,
	rows: MemberRow[] | null | undefined,
): TeamFilterItem[] {
	const byId: Record<string, TeamFilterItem> = {};
	for (const item of existing || []) {
		if (item && item.id) byId[item.id] = { id: item.id, name: item.name || item.id };
	}
	for (const row of rows || []) {
		// The carrier team of a company-role invite is not a team anyone chose
		// this person for, so it is not a team to filter the directory by.
		if (!row || row.inviteOnlyStub) continue;
		for (const team of row.membershipTeams || []) {
			if (!team || !team.id) continue;
			const known: TeamFilterItem | undefined = byId[team.id];
			byId[team.id] = { id: team.id, name: team.name || (known ? known.name : team.id) };
		}
	}
	// A plain comparator rather than lodash: this module stays importable by
	// the jest harness, which has no default-import interop.
	return Object.keys(byId).map((id) => byId[id]).sort((a: TeamFilterItem, b: TeamFilterItem) => {
		const aName: string = a.name.toLowerCase();
		const bName: string = b.name.toLowerCase();
		if (aName !== bName) return aName < bName ? -1 : 1;
		if (a.id === b.id) return 0;
		return a.id < b.id ? -1 : 1;
	});
}

/**
 * The minimal view of a bound `teams/{teamId}/teamMembers/{email}` doc the
 * directory reads. Kept structural so specs can pass plain objects and the
 * row can pass whatever vuefire resolved.
 */
export interface MembershipDoc {
	acceptedAt?: TimestampLike | null;
	companyUserRoleDefId?: string | null;
	// True while this pending invite exists only to carry a company role.
	// Cleared on acceptance, when it becomes an ordinary membership.
	companyRoleInviteOnly?: boolean | null;
}

/**
 * A bound membership is one of three things: `undefined` (the bind has not
 * resolved yet), `null` (vuefire resolved it and the doc does not exist, or
 * it stopped existing), or the doc.
 */
export type BoundMembership = MembershipDoc | null | undefined;
export type MembershipsMap = Record<string, BoundMembership>;

export type MemberRowState = 'loading' | 'live' | 'removed';

/**
 * The teams whose membership bind was refused or failed, as an immutable set
 * (`{ [teamId]: true }`). A rejected bind writes nothing into its key, so
 * without this the key would read as "still loading" forever.
 */
export type FailedTeamIdsMap = Record<string, true>;

/**
 * The teams a directory row watches: the first MEMBERS_DIRECTORY_MAX_TEAM_CHIPS
 * of the row's teams, in row order. Only that many chips ever render, and a
 * company admin who is on every team of a large company would otherwise open
 * one listener per team, on up to 100 rows a page.
 */
export function boundMemberTeamIds(row: MemberRow | null | undefined): string[] {
	return ((row && row.membershipTeams) || []).slice(0, MEMBERS_DIRECTORY_MAX_TEAM_CHIPS).map((team) => team.id);
}

/**
 * The failed set after a membership bind rejected: the team is recorded so
 * its key counts as resolved. Returns the set unchanged (same reference) when
 * the failure is stale: the row no longer watches the team, the key resolved
 * in the meantime, or it is already recorded.
 */
export function failedTeamIdsAfterBindFailure(
	failedTeamIds: FailedTeamIdsMap,
	memberships: MembershipsMap,
	teamId: string,
): FailedTeamIdsMap {
	if (!Object.prototype.hasOwnProperty.call(memberships, teamId)) return failedTeamIds;
	if (memberships[teamId] !== undefined) return failedTeamIds;
	if (failedTeamIds[teamId]) return failedTeamIds;
	return {
		...failedTeamIds,
		[teamId]: true,
	};
}

export interface MemberRowStateOptions {
	// How many teams the Postgres row names, bound or not.
	totalTeams: number;
	failedTeamIds: FailedTeamIdsMap;
	// The Postgres flag: a company-role holder on none of the company's teams.
	onNoTeam: boolean;
	// What the company-role cell bound: undefined until it reports (or when
	// there is nothing live to report), null once the role doc is gone.
	liveCompanyRoleDefId: string | null | undefined;
	// The Postgres flag: the person holds a company role. Only consulted until
	// the cell has reported, after which the live doc is the answer.
	hasCompanyRole?: boolean;
}

/**
 * Whether the person is known to hold a company role right now: the live doc
 * once the cell has reported it, the Postgres flag until then.
 */
function holdsCompanyRole(options: MemberRowStateOptions): boolean {
	if (options.liveCompanyRoleDefId === undefined) return !!options.hasCompanyRole;
	return !!options.liveCompanyRoleDefId;
}

/**
 * The row's own state, from its bindings alone.
 *
 * A row with no memberships to bind (a company-role holder on no team) lives
 * and dies by its company role doc: removed once the cell reports that doc
 * gone, live otherwise, and NEVER loading. The loading branch of the row
 * template mounts no company-role cell (the live and removed branches do), so
 * a loading state here could never end.
 *
 * Otherwise: live as soon as ANY bound membership has resolved to a doc (each
 * chip still loading shows its own skeleton, so one slow bind does not hold
 * the whole row on a skeleton); loading while any bound id is unresolved and
 * has not failed; and, once every bound id has settled without a doc, removed
 * only when every membership the row names was actually watched and came back
 * gone. Teams beyond the bound window and binds that failed were never
 * watched, and nothing can be concluded about a membership nobody watched, so
 * the row stays live.
 *
 * Even then, someone who still holds a company role is a real directory row
 * (a company-role holder on no team, decision 3 of the plan), so the row stays
 * live, reads as on no team, and keeps its company-role actions. Losing the
 * last membership in another tab must not take the role's menu away.
 */
export function memberRowState(
	memberships: MembershipsMap,
	boundTeamIds: string[],
	options: MemberRowStateOptions,
): MemberRowState {
	if (!boundTeamIds.length) {
		return options.onNoTeam && options.liveCompanyRoleDefId === null ? 'removed' : 'live';
	}

	let anyLoading: boolean = false;
	let anyFailed: boolean = false;
	for (const teamId of boundTeamIds) {
		const membership: BoundMembership = memberships[teamId];
		if (membership) return 'live';
		if (membership === null) continue;
		if (options.failedTeamIds[teamId]) anyFailed = true;
		else anyLoading = true;
	}
	if (anyLoading) return 'loading';
	if (anyFailed || options.totalTeams > boundTeamIds.length) return 'live';
	return holdsCompanyRole(options) ? 'live' : 'removed';
}

/**
 * True when the row names teams, every one of them is watched, and every
 * membership has resolved to nothing: the person has left (or been removed
 * from) every team the row knew about. Whether that makes the row removed or
 * a company-role holder on no team is `memberRowState`'s call; this is what
 * the Teams cell and the Joined column read to say "on no team" meanwhile.
 * `boundTeamIds` is given by a caller that watches only some of the teams.
 */
export function everyNamedTeamGone(
	row: MemberRow | null | undefined,
	memberships: MembershipsMap,
	boundTeamIds?: string[],
): boolean {
	const teams: MemberTeamRef[] = (row && row.membershipTeams) || [];
	if (!teams.length) return false;
	if (boundTeamIds && teams.length > boundTeamIds.length) return false;
	for (const team of teams) {
		if (boundTeamIds && boundTeamIds.indexOf(team.id) === -1) return false;
		if (memberships[team.id] !== null) return false;
	}
	return true;
}

export interface ResolvedMembership {
	team: MemberTeamRef;
	membership: MembershipDoc;
}

/**
 * The live memberships in the row's own team order, skipping anything still
 * loading or already gone.
 */
export function resolvedMemberships(memberships: MembershipsMap, teams: MemberTeamRef[]): ResolvedMembership[] {
	const out: ResolvedMembership[] = [];
	for (const team of teams) {
		const membership: BoundMembership = memberships[team.id];
		if (membership) out.push({ team, membership });
	}
	return out;
}

/**
 * A company-role invite stub is a real `teamMembers` doc on a carrier team,
 * created because a company role belongs to no team and an unregistered email
 * needs an invitation to carry one. The membership is real, and accepting it
 * does put the person on that team, but nobody set out to add them to it, so
 * the directory shows them as pending with no teams rather than implying a
 * team decision that was never made. The live doc decides once it has resolved
 * (acceptance clears the flag and the membership becomes ordinary); until then
 * the Postgres row's `inviteOnlyStub` does, which is true only when every
 * membership is a stub.
 */
export function isInviteOnlyStubMembership(row: MemberRow, membership: BoundMembership): boolean {
	if (membership) return membership.companyRoleInviteOnly === true;
	if (membership === null) return false;
	return !!row.inviteOnlyStub;
}

/**
 * The teams the person is shown as being on: the row's teams, in order, minus
 * the carrier teams of company-role-only invite stubs. The stubs stay bound
 * (they supply liveness and the intended company role); they are just not
 * presented as teams.
 */
export function visibleMemberTeams(row: MemberRow, memberships: MembershipsMap): MemberTeamRef[] {
	return (row.membershipTeams || []).filter((team) => !isInviteOnlyStubMembership(row, memberships[team.id]));
}

/**
 * True when the person is shown as holding only a company-role invite: the
 * row names teams, and every one of them is a stub carrier (or already gone).
 * The directory cell then says "none" instead of leaving a blank.
 */
export function isCompanyRoleInviteOnly(row: MemberRow | null | undefined, memberships: MembershipsMap): boolean {
	if (!row || !(row.membershipTeams || []).length) return false;
	let sawStub: boolean = false;
	for (const team of row.membershipTeams) {
		const membership: BoundMembership = memberships[team.id];
		if (membership === null) continue;
		if (!isInviteOnlyStubMembership(row, membership)) return false;
		sawStub = true;
	}
	return sawStub;
}

export interface MemberDashboardTeamOptions {
	// The viewer's selected team. They are on it, so the rules let them read it.
	currentTeamId: string | null;
	// MANAGE_TEAMS or a system admin: the rules let them read any team.
	canOpenAnyTeam: boolean;
}

/**
 * The team whose member dashboard a directory row opens, or null when there is
 * none to open. That page lives under one team: it reads `teams/{teamId}` and
 * the person's membership there, so it needs a team the person is shown as
 * being on and the viewer may read. That is the viewer's selected team when
 * the person is on it, and otherwise, for a viewer who may read any team, the
 * first one the person has joined (a joined membership's dashboard lists their
 * teams, a pending invite's cannot), or failing that their first invitation.
 * Someone shown as on no team has no such page, and their company role is
 * managed from the row's own menu.
 */
export function memberDashboardTeamId(
	row: MemberRow | null | undefined,
	memberships: MembershipsMap,
	options: MemberDashboardTeamOptions,
): string | null {
	if (!row || !row.email) return null;
	const teams: MemberTeamRef[] = visibleMemberTeams(row, memberships)
		.filter((team) => !!team.id && memberships[team.id] !== null);
	if (options.currentTeamId && teams.some((team) => team.id === options.currentTeamId)) {
		return options.currentTeamId;
	}
	if (!options.canOpenAnyTeam || !teams.length) return null;
	// Joined as the chips say it: the live doc once bound, the Postgres ref until then.
	const joined: MemberTeamRef | undefined = teams.find((team) => {
		const membership: BoundMembership = memberships[team.id];
		return membership ? !!membership.acceptedAt : !team.pending;
	});
	return (joined || teams[0]).id;
}

export type MemberTeamChipState = 'loading' | 'live' | 'static';

export interface MemberTeamChip {
	team: MemberTeamRef;
	// loading: the bind has not resolved, show a skeleton. live: drawn from the
	// bound doc. static: the bind failed, drawn from the Postgres ref.
	state: MemberTeamChipState;
	pending: boolean;
}

export interface MemberTeamChips {
	chips: MemberTeamChip[];
	// Teams the person is shown as being on that have no chip: the "+N more".
	hiddenCount: number;
}

/**
 * The Teams cell. Only the bound window (`boundTeamIds`) can have chips,
 * since a chip for a team nobody watches could only ever be a skeleton. Inside
 * the window a membership that is gone has no chip, a stub carrier has none
 * either, and a failed bind is drawn from the Postgres ref (its name, and
 * pending from the ref's own flag) instead of a skeleton that never ends.
 * Every visible team outside the window is counted into "+N more".
 */
export function memberTeamChips(
	row: MemberRow | null | undefined,
	memberships: MembershipsMap,
	failedTeamIds: FailedTeamIdsMap,
	boundTeamIds: string[],
): MemberTeamChips {
	const chips: MemberTeamChip[] = [];
	let hiddenCount: number = 0;
	if (!row) return { chips, hiddenCount };

	for (const team of visibleMemberTeams(row, memberships)) {
		if (boundTeamIds.indexOf(team.id) === -1) {
			hiddenCount++;
			continue;
		}
		const membership: BoundMembership = memberships[team.id];
		if (membership === null) continue;
		if (membership) {
			chips.push({ team, state: 'live', pending: !membership.acceptedAt });
		} else if (failedTeamIds[team.id]) {
			chips.push({ team, state: 'static', pending: !!team.pending });
		} else {
			chips.push({ team, state: 'loading', pending: !!team.pending });
		}
	}
	return { chips, hiddenCount };
}

/**
 * The handful of timestamp shapes a membership can carry: a Firestore
 * Timestamp (`toMillis`), its plain-object form (`seconds`), a Date, epoch
 * millis, or an ISO string from Postgres.
 */
export type TimestampLike =
	| { toMillis: () => number }
	| { seconds: number; nanoseconds?: number }
	| Date
	| number
	| string;

export function timestampToMillis(value: TimestampLike | null | undefined): number | null {
	if (value === null || value === undefined) return null;
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	if (typeof value === 'string') {
		const parsed: number = Date.parse(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	if (value instanceof Date) {
		const ms: number = value.getTime();
		return Number.isFinite(ms) ? ms : null;
	}
	if (typeof (value as { toMillis?: unknown }).toMillis === 'function') {
		const ms: number = (value as { toMillis: () => number }).toMillis();
		return Number.isFinite(ms) ? ms : null;
	}
	if (typeof (value as { seconds?: unknown }).seconds === 'number') {
		return (value as { seconds: number }).seconds * 1000;
	}
	return null;
}

export interface MemberDisplay {
	isPending: boolean;
	// Epoch millis of the earliest accepted membership, or null while pending.
	joinedAt: number | null;
	// The company role a pending invite will grant on acceptance, from the
	// stub on the membership doc. Only the live docs carry it.
	companyUserRoleDefIdFromStub: string | null;
}

/**
 * What the row shows about the person: derived from the live membership docs
 * once EVERY team the row names has settled (to a doc or to nothing), and
 * from the docs and the Postgres row together until then. Pending means no
 * membership is accepted, the same derivation the endpoint uses.
 *
 * Until every team has settled the docs cannot overrule a Postgres row that
 * says joined: binds resolve one at a time, and a pending membership that
 * happens to resolve first (or an accepted one whose bind failed) would
 * otherwise flash, or pin, a Pending chip on someone who joined long ago and
 * move the Joined date as each doc lands. A team is unsettled while its key is
 * `undefined`: still loading, failed (a rejected bind writes nothing), or
 * never watched at all.
 *
 * `boundTeamIds` is given by a caller that watches only some of the row's
 * teams (the directory row). The watched docs then cannot overrule Postgres
 * about the rest: someone pending on every watched team but joined on an
 * unwatched one is still joined, and their earliest accepted-at may sit on a
 * team nobody watches. A caller that binds every team omits it.
 */
export function memberDisplay(row: MemberRow, memberships: MembershipsMap, boundTeamIds?: string[]): MemberDisplay {
	const live: ResolvedMembership[] = resolvedMemberships(memberships, row.membershipTeams || []);

	if (!live.length) {
		// Every team the row names is known to be gone: a company-role holder
		// who is on no team any more. Neither a join date nor "Pending" is true.
		if (everyNamedTeamGone(row, memberships, boundTeamIds)) {
			return {
				isPending: false,
				joinedAt: null,
				companyUserRoleDefIdFromStub: null,
			};
		}
		return {
			isPending: !!row.isPending,
			joinedAt: timestampToMillis(row.joined),
			companyUserRoleDefIdFromStub: null,
		};
	}

	let earliestJoined: number | null = null;
	let stubRoleDefId: string | null = null;
	for (const { membership } of live) {
		const accepted: number | null = timestampToMillis(membership.acceptedAt);
		if (accepted !== null && (earliestJoined === null || accepted < earliestJoined)) {
			earliestJoined = accepted;
		}
		if (!stubRoleDefId && membership.companyUserRoleDefId) {
			stubRoleDefId = membership.companyUserRoleDefId;
		}
	}

	const hasUnwatchedTeams: boolean = !!boundTeamIds && (row.membershipTeams || []).length > boundTeamIds.length;
	const hasUnsettledTeams: boolean = (row.membershipTeams || []).some((team) => memberships[team.id] === undefined);
	const postgresStillCounts: boolean = (hasUnwatchedTeams || hasUnsettledTeams) && !row.isPending;
	if (postgresStillCounts) {
		const rowJoined: number | null = timestampToMillis(row.joined);
		if (rowJoined !== null && (earliestJoined === null || rowJoined < earliestJoined)) {
			earliestJoined = rowJoined;
		}
	}

	return {
		// A row Postgres calls joined stays joined until the docs can prove
		// otherwise, even in the odd case that it carries no join date.
		isPending: earliestJoined === null && !postgresStillCounts,
		joinedAt: earliestJoined,
		companyUserRoleDefIdFromStub: stubRoleDefId,
	};
}

/**
 * What the Joined column says. `none` is the registered company-role holder
 * on no team: not pending (there is no invite to accept) and never joined a
 * team, so neither a date nor "Pending" would be true.
 */
export type MemberJoinedState = 'joined' | 'pending' | 'none';

export function memberJoinedState(display: MemberDisplay): MemberJoinedState {
	if (display.joinedAt !== null) return 'joined';
	return display.isPending ? 'pending' : 'none';
}

/** A selected person in the Directory's CSV: one readable value per column, keyed by header value. */
export interface MemberDownloadRow {
	email: string;
	teams: string;
	companyRole: string;
	joined: string;
}

export function memberDownloadRow(row: MemberRow, companyRoleName: string | null | undefined): MemberDownloadRow {
	return {
		email: row.email,
		// The carrier team of a company-role invite is not a team they were put on, as in the Teams column.
		// A team they are only invited to says so, or it would read the same as one they joined.
		teams: row.inviteOnlyStub
			? ''
			: (row.membershipTeams || [])
				.filter((team: MemberTeamRef) => !!team.name)
				.map((team: MemberTeamRef) => (team.pending ? `${team.name} (pending)` : team.name))
				.join('; '),
		companyRole: companyRoleName || '',
		joined: row.isPending ? 'Pending' : (row.joined ? moment(row.joined).format('YYYY-MM-DD') : ''),
	};
}

/**
 * The teams "Remove from all Teams" takes the person off: the ones they are
 * shown as being on, so never a company-role invite's carrier team (that
 * membership is how the invite carries the role, and cancelling it is the
 * company role's business), and never a membership already known to be gone.
 * A team past the chip limit is not bound, so the row's own `inviteOnlyStub`
 * answers for it, exactly as it does in the Teams column.
 */
export function removableMemberTeams(row: MemberRow, memberships: MembershipsMap): MemberTeamRef[] {
	return visibleMemberTeams(row, memberships).filter((team: MemberTeamRef) => memberships[team.id] !== null);
}

/**
 * What a row tells the page about its company role: the live role once its
 * cell has read it, and until then the stub's role or the Postgres flag. The
 * page keeps one per row for the bulk actions and the CSV, which see no cells.
 */
export interface MemberCompanyRoleReport {
	companyUserRoleDefId: string | null;
	hasCompanyRole: boolean;
}

export type MemberCompanyRoleReports = Record<string, MemberCompanyRoleReport>;

/**
 * The reports worth keeping, after the rows change: only the given keys (the
 * rows on screen and the ones still selected). A copy, never the map edited
 * in place, so Vue sees the change.
 */
export function keepMemberCompanyRoleReports(reports: MemberCompanyRoleReports, keys: string[]): MemberCompanyRoleReports {
	const kept: MemberCompanyRoleReports = {};
	for (const key of keys) {
		if (reports[key]) kept[key] = reports[key];
	}
	return kept;
}

/**
 * A selected row as the bulk company role dialog plans it. A row that has not
 * reported yet falls back to the Postgres flag, with no role to compare, so an
 * Assign is never skipped on a guess.
 */
export function bulkCompanyRoleTargetFromRow(row: MemberRow, report: MemberCompanyRoleReport | null | undefined): BulkCompanyRoleTarget {
	return {
		email: row.email,
		userId: row.userId || null,
		companyUserRoleDefId: report ? report.companyUserRoleDefId : null,
		hasCompanyRole: report ? report.hasCompanyRole : !!row.hasCompanyRole,
	};
}

/** How many teams the "Remove from all Teams" dialog names before it counts the rest. */
export const REMOVE_FROM_ALL_TEAMS_NAMED_MAX = 5;

/**
 * The teams by name, for the "Remove from all Teams" dialog, where nobody
 * picked them: "Design, Marketing and Sales", or past the limit "Design,
 * Marketing, ... and 4 more". A team without a name is counted, not named.
 */
export function removeFromAllTeamsNames(
	teams: Array<{ name?: string | null } | null | undefined> | null | undefined,
	max: number = REMOVE_FROM_ALL_TEAMS_NAMED_MAX,
): string {
	const all = teams || [];
	const names: string[] = all
		.map((team) => (team && typeof team.name === 'string' ? team.name.trim() : ''))
		.filter((name: string) => !!name);
	const named: string[] = names.slice(0, Math.max(1, max));
	const rest: number = all.length - named.length;
	if (!named.length) return '';
	if (rest > 0) return `${named.join(', ')} and ${rest} more`;
	if (named.length === 1) return named[0];
	return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
}

export interface JustAddedDirectoryInviteInput {
	email: string;
	invitedIso: string;
	// The Team the invitation that carries the company role is on
	team: { id: string; name: string } | null;
}

/**
 * An address just given a company role on this page, with no account yet, so
 * the role went out as an invitation: the Directory's row for it, to pin there
 * until the listing has it (utils/justAdded.ts). Its row binds the invitation
 * on that Team, so it goes live like any other.
 */
export function justAddedDirectoryInviteRow(input: JustAddedDirectoryInviteInput): MemberRow {
	const key: string = input.email.trim().toLowerCase();
	const membershipTeams: MemberTeamRef[] = input.team
		? [{ id: input.team.id, name: input.team.name, pending: true, isActive: true }]
		: [];
	return {
		key,
		email: key,
		userId: null,
		isPending: true,
		joined: null,
		firstInvited: input.invitedIso,
		membershipTeams,
		membershipCount: membershipTeams.length,
		hasCompanyRole: true,
		// Created by the company role, rather than by inviting them to the Team
		inviteOnlyStub: true,
		onNoTeam: !input.team,
	};
}
